import React from 'react';
import ReactDOM from 'react-dom/client';
import { AuthProvider } from './AuthContext';
import App from './App';
import { AppThemeProvider } from './app/AppThemeContext';
import { safeSessionSet } from './browserStorage';
import { capturePendingClassRoute } from './classSeasons/classEntryRouting';
import { FEATURE_FLAGS } from './featureFlags';

if (typeof window !== 'undefined' && FEATURE_FLAGS.CLASS_SEASONS_ENABLED) {
  capturePendingClassRoute({
    hash: window.location.hash,
    pathname: window.location.pathname,
    replaceUrl: nextUrl => window.history.replaceState(window.history.state, '', nextUrl),
    search: window.location.search,
    setValue: safeSessionSet,
  });
}

const WeeklyRecapQaHarness = React.lazy(() => import('./app/WeeklyRecapQaHarness'));
const ChallengeCreateQaHarness = React.lazy(() => import('./challenges/ChallengeCreateQaHarness'));
const ActiveWorkoutQaHarness = React.lazy(() => import('./workouts/presentation/ActiveWorkoutQaHarness'));
const EmberOnboardingQaHarness = React.lazy(() => import('./workouts/presentation/tab/hub/EmberOnboardingQaHarness'));
const WorkoutsHubQaHarness = process.env.NODE_ENV === 'development'
  ? React.lazy(() => import('./workouts/presentation/tab/hub/WorkoutsHubQaHarness'))
  : null;
const ProgressOverviewQaHarness = React.lazy(() => import('./workouts/presentation/progress-overhaul/ProgressOverviewQaHarness'));
const ActivationOnboardingQaHarness = React.lazy(() => import('./ActivationOnboardingQaHarness'));
const NotificationActionCenterQaHarness = React.lazy(() => import('./notifications/NotificationActionCenterQaHarness'));
const MemberClassQaHarness = React.lazy(() => import('./classSeasons/MemberClassQaHarness'));
const ClassesStaffQaHarness = React.lazy(() => import('./classSeasons/ClassesStaffQaHarness'));

function QaHarnessBoundary({ children }) {
  return (
    <React.Suspense fallback={<div style={{ minHeight: '100vh', background: '#080808' }} />}>
      {children}
    </React.Suspense>
  );
}

function getWeeklyRecapQaConfig() {
  if (typeof window === "undefined") return null;
  const params = new URLSearchParams(window.location.search);
  if (params.get("weeklyRecapQa") !== "1") return null;

  const theme = params.get("theme");
  if (theme === "day" || theme === "night") {
    window.localStorage?.setItem("tribelog.appearance", theme);
  }

  return {
    autoOpen: params.get("weeklyRecapAutoOpen") === "1",
    state: params.get("weeklyRecapState") || "endweek",
    theme: theme === "day" || theme === "night" ? theme : null,
  };
}

const root = ReactDOM.createRoot(document.getElementById('root'));
const weeklyRecapQaConfig = getWeeklyRecapQaConfig();
const workoutsHubQaEnabled = process.env.NODE_ENV === 'development'
  && ['localhost', '127.0.0.1'].includes(window.location.hostname)
  && new URLSearchParams(window.location.search).get('workoutsHubQa') === '1';
if (workoutsHubQaEnabled) {
  const theme = new URLSearchParams(window.location.search).get('theme');
  if (theme === 'day' || theme === 'night') window.localStorage.setItem('tribelog.appearance', theme);
}
const challengeCreateQaEnabled = typeof window !== "undefined"
  && new URLSearchParams(window.location.search).get("challengeCreateQa") === "1";
const activeWorkoutQaEnabled = typeof window !== "undefined"
  && new URLSearchParams(window.location.search).get("activeWorkoutQa") === "1";
const emberOnboardingQaEnabled = typeof window !== "undefined"
  && new URLSearchParams(window.location.search).get("emberOnboardingQa") === "1";
const progressOverviewQaEnabled = typeof window !== "undefined"
  && new URLSearchParams(window.location.search).get("progressOverviewQa") === "1";
const activationOnboardingQaEnabled = typeof window !== "undefined"
  && new URLSearchParams(window.location.search).get("activationOnboardingQa") === "1";
const notificationActionCenterQaEnabled = typeof window !== "undefined"
  && new URLSearchParams(window.location.search).get("notificationActionCenterQa") === "1";
const memberClassQaEnabled = typeof window !== 'undefined'
  && new URLSearchParams(window.location.search).get('memberClassQa') === '1';
const classesStaffQaEnabled = typeof window !== 'undefined'
  && new URLSearchParams(window.location.search).get('classesStaffQa') === '1';
if ((challengeCreateQaEnabled || activeWorkoutQaEnabled || emberOnboardingQaEnabled || progressOverviewQaEnabled || activationOnboardingQaEnabled || notificationActionCenterQaEnabled || memberClassQaEnabled || classesStaffQaEnabled) && typeof window !== "undefined") {
  const theme = new URLSearchParams(window.location.search).get("theme");
  if (theme === "day" || theme === "night") {
    window.localStorage?.setItem("tribelog.appearance", theme);
  }
}

if (workoutsHubQaEnabled) {
  root.render(<React.StrictMode><QaHarnessBoundary><AppThemeProvider><WorkoutsHubQaHarness /></AppThemeProvider></QaHarnessBoundary></React.StrictMode>);
} else if (classesStaffQaEnabled) {
  const params = new URLSearchParams(window.location.search);
  root.render(
    <React.StrictMode>
      <QaHarnessBoundary><ClassesStaffQaHarness largeText={params.get('largeText') === '1'} screen={params.get('screen') || 'today'} /></QaHarnessBoundary>
    </React.StrictMode>
  );
} else if (memberClassQaEnabled) {
  const params = new URLSearchParams(window.location.search);
  root.render(
    <React.StrictMode>
      <QaHarnessBoundary><MemberClassQaHarness largeText={params.get('largeText') === '1'} screen={params.get('screen') || 'list'} /></QaHarnessBoundary>
    </React.StrictMode>
  );
} else if (notificationActionCenterQaEnabled) {
  const params = new URLSearchParams(window.location.search);
  root.render(
    <React.StrictMode>
      <QaHarnessBoundary>
        <AppThemeProvider>
          <NotificationActionCenterQaHarness
            largeText={params.get("largeText") === "1"}
            screen={params.get("screen") || "action"}
          />
        </AppThemeProvider>
      </QaHarnessBoundary>
    </React.StrictMode>
  );
} else if (activationOnboardingQaEnabled) {
  root.render(
    <React.StrictMode>
      <QaHarnessBoundary>
        <AppThemeProvider>
          <ActivationOnboardingQaHarness />
        </AppThemeProvider>
      </QaHarnessBoundary>
    </React.StrictMode>
  );
} else if (progressOverviewQaEnabled) {
  root.render(
    <React.StrictMode>
      <QaHarnessBoundary>
        <AppThemeProvider>
          <ProgressOverviewQaHarness />
        </AppThemeProvider>
      </QaHarnessBoundary>
    </React.StrictMode>
  );
} else if (emberOnboardingQaEnabled) {
  const params = new URLSearchParams(window.location.search);
  root.render(
    <React.StrictMode>
      <QaHarnessBoundary>
        <AppThemeProvider>
          <EmberOnboardingQaHarness screen={params.get("screen") || "intro"} />
        </AppThemeProvider>
      </QaHarnessBoundary>
    </React.StrictMode>
  );
} else if (activeWorkoutQaEnabled) {
  root.render(
    <React.StrictMode>
      <QaHarnessBoundary>
        <AppThemeProvider>
          <ActiveWorkoutQaHarness />
        </AppThemeProvider>
      </QaHarnessBoundary>
    </React.StrictMode>
  );
} else if (challengeCreateQaEnabled) {
  root.render(
    <React.StrictMode>
      <QaHarnessBoundary>
        <ChallengeCreateQaHarness />
      </QaHarnessBoundary>
    </React.StrictMode>
  );
} else if (weeklyRecapQaConfig) {
  root.render(
    <React.StrictMode>
      <QaHarnessBoundary>
        <AppThemeProvider>
          <WeeklyRecapQaHarness {...weeklyRecapQaConfig} />
        </AppThemeProvider>
      </QaHarnessBoundary>
    </React.StrictMode>
  );
} else {
  root.render(
    <React.StrictMode>
      <AuthProvider>
        <App />
      </AuthProvider>
    </React.StrictMode>
  );
}
